import type {
  ChangeFormField,
  ChangeSubtask,
  ChangeTypePolicyFlags,
  ChangeWorkGroup,
} from '../../types/changes';
import { changesApi } from '../../services/changesApi';
import {
  createLikePackageGroupIds,
  flattenInternalSlots,
  internalReviewKey,
  internalSlotLabel,
  externalLineKey,
  parseExternalLineKey,
  occupyingExternalGroupIds,
  occupyingInternalReviewKeys,
  sortSubtasksActiveFirst,
  resolveReviewIncidentWorkflow,
  resolveInternalSlotByReviewKey,
  slotReviewKey,
} from './changeCreateReview';
import { ASSIGNMENT_PESSOA, ASSIGNMENT_SETOR } from './changeSubtaskAssignment';
import { validateAttachmentFile } from '../../utils/uploadValidation';
import {
  emptyIncidentDraft,
  incidentAllowsAttachments,
  incidentCustomFieldsPayload,
  type IncidentWorkflowOption,
} from './incidentQuickSend';
import {
  hydrateIncidentCustomFieldValues,
  uploadIncidentCustomFieldFiles,
  validateIncidentTicketFields,
  type IncidentCustomFieldFiles,
  type IncidentCustomFieldValues,
} from './incidentTicketFields';
import type { ExternalReviewDraft, InternalReviewDraft } from './components/ChangeCreateSubtaskReview';
import {
  INSTANCE_CHECKLIST_FIELD_TYPES,
  parseQuestionOptions,
  templateFieldsToReviewQuestions,
} from './changeInstanceChecklistFields';
import {
  persistInternalDraftIfNeeded,
  type InternalSubtaskTemplatePersistMode,
} from './changePersistInternalSubtaskTemplate';
import { canMutateInternalPackageSlot } from './changeInternalSlotOwnership';

export const INTERNAL_QUESTION_FIELD_TYPES = INSTANCE_CHECKLIST_FIELD_TYPES;

export const emptyInternalDraft = (group: ChangeWorkGroup): InternalReviewDraft => ({
  selected: true,
  title:
    (group.internal_subtask_template_name || group.name || '').trim() || 'Checklist',
  comments: '',
  task_type_id: '',
  assignment_kind: 'setor',
  assignee_id: '',
  questions: [],
});

export const emptyExternalDraft = (
  group: ChangeWorkGroup,
  workflows: IncidentWorkflowOption[] = [],
): ExternalReviewDraft => {
  const base = emptyIncidentDraft(group);
  const workflowId = resolveReviewIncidentWorkflow(group, workflows) || base.workflow_id;
  return { selected: true, ...base, workflow_id: workflowId };
};

const positiveIntOrUndefined = (value: number | '' | undefined): number | undefined => {
  if (typeof value === 'number' && value > 0) return value;
  return undefined;
};

export async function validateSubtaskPackage(opts: {
  selectedInternals: ChangeWorkGroup[];
  selectedExternals: ChangeWorkGroup[];
  internalDrafts: Record<string, InternalReviewDraft>;
  externalDrafts: Record<string, ExternalReviewDraft>;
  workflows: IncidentWorkflowOption[];
}): Promise<{ preparedExternals: Record<string, ExternalReviewDraft> } | { error: string }> {
  const { selectedInternals, selectedExternals, internalDrafts, externalDrafts, workflows } = opts;
  const preparedExternals: Record<string, ExternalReviewDraft> = {};

  for (const group of selectedInternals) {
    const internalKey = group.review_key || slotReviewKey(group);
    const draft = internalDrafts[internalKey] ?? emptyInternalDraft(group);
    const label = internalSlotLabel(group);
    if (!(draft.title || group.internal_subtask_template_name || group.name || '').trim()) {
      return { error: `Informe o título da subtarefa de ${label}.` };
    }
    if (draft.assignment_kind === 'pessoa' && !positiveIntOrUndefined(draft.assignee_id)) {
      return { error: `Escolha a pessoa da subtarefa de ${label}.` };
    }
    for (const question of draft.questions || []) {
      if (!question.label.trim()) {
        return { error: `Há pergunta sem enunciado na subtarefa de ${label}.` };
      }
      if (!INTERNAL_QUESTION_FIELD_TYPES.has(question.field_type)) {
        return {
          error: `${label}: o tipo «${question.field_type}» não pode ser enviado. Troque o tipo da pergunta.`,
        };
      }
      if (question.field_type === 'list' && parseQuestionOptions(question.options).length < 2) {
        return { error: `A lista da subtarefa de ${label} precisa de ao menos duas opções.` };
      }
    }
  }

  for (const group of selectedExternals) {
    const lineKey = externalLineKey(group);
    let draft = externalDrafts[lineKey] ?? emptyExternalDraft(group, workflows);
    const workflowId = Number(
      draft.workflow_id || resolveReviewIncidentWorkflow(group, workflows) || 0,
    );
    if (!Number.isFinite(workflowId) || workflowId <= 0) {
      return { error: `Escolha o fluxo de incidentes de ${group.name}.` };
    }
    if (!draft.workflow_id) {
      draft = { ...draft, workflow_id: workflowId };
    }
    if (!draft.customFields?.length) {
      try {
        const fields = await changesApi.listIncidentWorkflowFields(
          workflowId,
          draft.incidents_group_id,
        );
        draft = {
          ...draft,
          customFields: fields,
          customFieldValues: {
            ...hydrateIncidentCustomFieldValues(group.incident_custom_field_defaults),
            ...draft.customFieldValues,
          },
        };
      } catch {
        return {
          error: `Não foi possível carregar os campos do chamado de ${group.name}. Abra Editar e tente de novo.`,
        };
      }
    }
    if (!draft.incidents_group_id) {
      return { error: `Escolha o grupo para encaminhar o chamado de ${group.name}.` };
    }
    if (!(draft.title || '').trim()) {
      return { error: `Informe o título do chamado de ${group.name}.` };
    }
    if (!(draft.comments || '').trim()) {
      return { error: `Informe a descrição do chamado de ${group.name}.` };
    }
    if (!(draft.urgency || '').trim()) {
      return { error: `Informe a urgência do chamado de ${group.name}.` };
    }
    if (!incidentAllowsAttachments(group) && (draft.files || []).length) {
      return { error: `O chamado de ${group.name} não aceita anexo.` };
    }
    const customError = validateIncidentTicketFields(
      draft.customFields,
      draft.customFieldValues,
      draft.customFieldFiles,
    );
    if (customError) {
      return { error: `${group.name}: ${customError}. Use Editar no chamado para preencher.` };
    }
    const invalid = (draft.files || []).map(validateAttachmentFile).find(Boolean);
    if (invalid) {
      return { error: invalid };
    }
    preparedExternals[lineKey] = draft;
  }

  return { preparedExternals };
}

export function buildSubtaskPackagePayload(opts: {
  selectedInternals: ChangeWorkGroup[];
  selectedExternals: ChangeWorkGroup[];
  internalDrafts: Record<string, InternalReviewDraft>;
  preparedExternals: Record<string, ExternalReviewDraft>;
  workflows: IncidentWorkflowOption[];
}) {
  const { selectedInternals, selectedExternals, internalDrafts, preparedExternals, workflows } =
    opts;
  return {
    internal_subtasks: selectedInternals.map((group) => {
      const internalKey = group.review_key || slotReviewKey(group);
      const draft = internalDrafts[internalKey] ?? emptyInternalDraft(group);
      return {
        group_id: group.group_id,
        template_id: group.internal_subtask_template_id || undefined,
        title: (draft.title || group.internal_subtask_template_name || group.name || '').trim(),
        comments: draft.comments?.trim() || undefined,
        task_type_id: positiveIntOrUndefined(draft.task_type_id),
        assignment_kind: draft.assignment_kind || 'setor',
        assignee_id:
          draft.assignment_kind === 'pessoa' ? positiveIntOrUndefined(draft.assignee_id) : undefined,
        questions: (draft.questions || []).map((question) => ({
          label: question.label.trim(),
          field_type: question.field_type,
          is_required: question.is_required,
          help_text: question.help_text?.trim() || undefined,
          options:
            question.field_type === 'list' || question.field_type === 'yes_no'
              ? parseQuestionOptions(question.options)
              : [],
        })),
      };
    }),
    incident_subtasks: selectedExternals.map((group) => {
      const draft = preparedExternals[externalLineKey(group)];
      const workflowId = Number(
        draft.workflow_id || resolveReviewIncidentWorkflow(group, workflows),
      );
      return {
        group_id: group.group_id,
        workflow_id: workflowId,
        incidents_group_id: draft.incidents_group_id || undefined,
        title: draft.title.trim(),
        comments: draft.comments.trim(),
        urgency: draft.urgency,
        has_attachment: (draft.files || []).length > 0,
        gates_implementar: false,
        ...incidentCustomFieldsPayload(draft),
      };
    }),
  };
}

function normalizeAssignmentKind(kind?: string | null): typeof ASSIGNMENT_SETOR | typeof ASSIGNMENT_PESSOA {
  return kind === ASSIGNMENT_PESSOA ? ASSIGNMENT_PESSOA : ASSIGNMENT_SETOR;
}

/** Campos só desta instância (não cópia do modelo no snapshot do catálogo). */
const INSTANCE_CHECKLIST_SORT_ORDER_MIN = 1000;

function checklistItemsToQuestions(items: ChangeFormField[] = []): InternalReviewDraft['questions'] {
  return items
    .filter((item) => INTERNAL_QUESTION_FIELD_TYPES.has(item.field_type))
    .map((item, index) => ({
      key: `field-${item.id}-${index}`,
      label: item.label,
      field_type: item.field_type,
      is_required: item.is_required,
      options: (item.options || []).join('\n'),
    }));
}

/** Redisparo: não reenviar perguntas já copiadas do modelo — o backend faz snapshot de novo. */
function checklistItemsToInstanceOnlyQuestions(
  items: ChangeFormField[] = [],
): InternalReviewDraft['questions'] {
  return checklistItemsToQuestions(
    items.filter((item) => (item.sort_order ?? 0) >= INSTANCE_CHECKLIST_SORT_ORDER_MIN),
  );
}

export function resolveWorkGroupSlotForSubtask(
  subtask: ChangeSubtask,
  workGroups: ChangeWorkGroup[],
): ChangeWorkGroup | null {
  const reviewKey = internalReviewKey(subtask.group_id, subtask.internal_template_id);
  const fromCatalog = flattenInternalSlots(workGroups).find((slot) => slotReviewKey(slot) === reviewKey);
  if (fromCatalog) return fromCatalog;
  const group = workGroups.find((item) => item.group_id === subtask.group_id);
  if (!group) return null;
  return {
    ...group,
    review_key: reviewKey,
    internal_subtask_template_id: subtask.internal_template_id ?? group.internal_subtask_template_id,
  };
}

export function internalDraftFromExistingSubtask(
  subtask: ChangeSubtask,
  opts?: { locked?: boolean },
): InternalReviewDraft {
  const assignmentKind = normalizeAssignmentKind(subtask.assignment_kind);
  const locked = opts?.locked ?? true;
  return {
    selected: true,
    locked,
    existingSubtaskId: locked ? subtask.id : undefined,
    title: (subtask.title || '').trim(),
    comments: subtask.comments?.trim() || '',
    task_type_id: subtask.task_type_id && subtask.task_type_id > 0 ? subtask.task_type_id : '',
    assignment_kind: assignmentKind,
    assignee_id:
      assignmentKind === ASSIGNMENT_PESSOA && subtask.assignee_id ? subtask.assignee_id : '',
    questions: locked
      ? checklistItemsToQuestions(subtask.checklist?.items)
      : checklistItemsToInstanceOnlyQuestions(subtask.checklist?.items),
  };
}

export function externalDraftFromExistingSubtask(
  subtask: ChangeSubtask,
  group: ChangeWorkGroup,
  workflows: IncidentWorkflowOption[] = [],
  opts?: { locked?: boolean },
): ExternalReviewDraft {
  const base = emptyExternalDraft(group, workflows);
  const locked = opts?.locked ?? true;
  return {
    ...base,
    selected: true,
    locked,
    existingSubtaskId: locked ? subtask.id : undefined,
    title: (subtask.title || base.title || '').trim(),
    comments: (subtask.comments || base.comments || '').trim(),
    urgency: base.urgency || '3',
    incidents_group_id: subtask.incident?.ticket_id
      ? base.incidents_group_id
      : group.incidents_group_id ?? base.incidents_group_id,
  };
}

export interface ExistingSubtaskPackageSeed {
  internalKeys: string[];
  externalIds: number[];
  internalDrafts: Record<string, InternalReviewDraft>;
  externalDrafts: Record<number, ExternalReviewDraft>;
}

/** Lista subtarefas já ativas na mudança para o modal Adicionar (somente leitura). */
export function seedPackageFromExistingSubtasks(
  subtasks: ChangeSubtask[] | null | undefined,
  workGroups: ChangeWorkGroup[],
  workflows: IncidentWorkflowOption[] = [],
): ExistingSubtaskPackageSeed {
  const internalKeys: string[] = [];
  const externalIds: number[] = [];
  const internalDrafts: Record<string, InternalReviewDraft> = {};
  const externalDrafts: Record<number, ExternalReviewDraft> = {};

  for (const subtask of subtasks || []) {
    if (subtask.invalidated_at) continue;
    if (subtask.origin === 'incidente') {
      const group = workGroups.find((item) => item.group_id === subtask.group_id);
      if (!group) continue;
      externalIds.push(group.group_id);
      externalDrafts[group.group_id] = externalDraftFromExistingSubtask(subtask, group, workflows);
      continue;
    }
    const slot = resolveWorkGroupSlotForSubtask(subtask, workGroups);
    if (!slot) continue;
    const key = slotReviewKey(slot);
    if (!internalKeys.includes(key)) {
      internalKeys.push(key);
    }
    internalDrafts[key] = internalDraftFromExistingSubtask(subtask);
  }

  return { internalKeys, externalIds, internalDrafts, externalDrafts };
}

function isSubtaskInvalidated(subtask: Pick<ChangeSubtask, 'invalidated_at'>): boolean {
  return Boolean(subtask.invalidated_at);
}

function redispatchInternalLineKey(
  subtask: ChangeSubtask,
  slot: ChangeWorkGroup,
  usedKeys: Set<string>,
): string {
  const base = slotReviewKey(slot);
  if (!isSubtaskInvalidated(subtask) && !usedKeys.has(base)) {
    return base;
  }
  const keyed = `${base}~${subtask.id}`;
  if (!usedKeys.has(keyed)) return keyed;
  return `${keyed}~${usedKeys.size}`;
}

function redispatchExternalLineKey(
  subtask: ChangeSubtask,
  usedKeys: Set<string>,
): string {
  const base = String(subtask.group_id);
  if (!isSubtaskInvalidated(subtask) && !usedKeys.has(base)) {
    return base;
  }
  const keyed = `${base}~${subtask.id}`;
  if (!usedKeys.has(keyed)) return keyed;
  return `${keyed}~${usedKeys.size}`;
}

export type ManualRedispatchPackageSeed = {
  internalKeys: string[];
  externalKeys: string[];
  internalDrafts: Record<string, InternalReviewDraft>;
  externalDrafts: Record<string, ExternalReviewDraft>;
};

/**
 * Redisparo manual na aba Subtarefas: repõe linhas desta mudança (ativas = só leitura;
 * canceladas/invalidadas = selecionáveis para nova rodada), sem sugerir comitê automático.
 */
export function seedPackageForManualRedispatch(
  subtasks: ChangeSubtask[] | null | undefined,
  workGroups: ChangeWorkGroup[],
  workflows: IncidentWorkflowOption[] = [],
): ManualRedispatchPackageSeed {
  const internalKeys: string[] = [];
  const externalKeys: string[] = [];
  const internalDrafts: Record<string, InternalReviewDraft> = {};
  const externalDrafts: Record<string, ExternalReviewDraft> = {};
  const usedInternal = new Set<string>();
  const usedExternal = new Set<string>();

  const ordered = sortSubtasksActiveFirst(subtasks || []);

  for (const subtask of ordered) {
    const locked = !isSubtaskInvalidated(subtask);
    if (subtask.origin === 'incidente') {
      const group = workGroups.find((item) => item.group_id === subtask.group_id);
      if (!group) continue;
      const lineKey = redispatchExternalLineKey(subtask, usedExternal);
      usedExternal.add(lineKey);
      externalKeys.push(lineKey);
      externalDrafts[lineKey] = externalDraftFromExistingSubtask(subtask, group, workflows, {
        locked,
      });
      continue;
    }
    const slot = resolveWorkGroupSlotForSubtask(subtask, workGroups);
    if (!slot) continue;
    const lineKey = redispatchInternalLineKey(subtask, slot, usedInternal);
    usedInternal.add(lineKey);
    internalKeys.push(lineKey);
    internalDrafts[lineKey] = internalDraftFromExistingSubtask(subtask, { locked });
  }

  return { internalKeys, externalKeys, internalDrafts, externalDrafts };
}

/**
 * Mescla subtarefas já na mudança com o pacote sugerido (como no passo «Confirme as subtarefas»).
 */
export function mergeExistingWithSuggestedPackage(
  subtasks: ChangeSubtask[] | null | undefined,
  workGroups: ChangeWorkGroup[],
  workflows: IncidentWorkflowOption[],
  opts: {
    changeType: string;
    allowInternal: boolean;
    allowExternal: boolean;
    policy?: ChangeTypePolicyFlags;
  },
): ExistingSubtaskPackageSeed {
  const existing = seedPackageFromExistingSubtasks(subtasks, workGroups, workflows);
  const occupiedInternal = occupyingInternalReviewKeys(subtasks);
  const occupiedExternal = occupyingExternalGroupIds(subtasks);
  const suggested = createLikePackageGroupIds(workGroups, {
    changeType: opts.changeType,
    allowInternal: opts.allowInternal,
    allowExternal: opts.allowExternal,
    workflows,
    occupiedGroupIds: occupiedExternal,
    occupiedInternalKeys: occupiedInternal,
    occupiedExternalIds: occupiedExternal,
    policy: opts.policy,
  });

  const internalKeys = [...existing.internalKeys];
  const internalDrafts: Record<string, InternalReviewDraft> = { ...existing.internalDrafts };
  for (const key of suggested.internalKeys) {
    if (internalKeys.includes(key)) continue;
    internalKeys.push(key);
    const slot = flattenInternalSlots(workGroups).find((item) => slotReviewKey(item) === key);
    if (slot) {
      internalDrafts[key] = emptyInternalDraft(slot);
    }
  }

  const externalIds = [...existing.externalIds];
  const externalDrafts: Record<number, ExternalReviewDraft> = { ...existing.externalDrafts };
  for (const groupId of suggested.externalIds) {
    if (externalIds.includes(groupId)) continue;
    externalIds.push(groupId);
    const group = workGroups.find((item) => item.group_id === groupId);
    if (group) {
      externalDrafts[groupId] = emptyExternalDraft(group, workflows);
    }
  }

  return { internalKeys, externalIds, internalDrafts, externalDrafts };
}

export async function enrichUnlockedInternalDrafts(
  workGroups: ChangeWorkGroup[],
  drafts: Record<string, InternalReviewDraft>,
): Promise<Record<string, InternalReviewDraft>> {
  const next = { ...drafts };
  const entries = Object.entries(drafts).filter(([, draft]) => !draft.locked);
  for (const [key, draft] of entries) {
    const slot = resolveInternalSlotByReviewKey(workGroups, key);
    if (!slot) continue;
    next[key] = await enrichInternalDraftFromTemplate(slot, draft);
  }
  return next;
}

export function filterNewPackageSelections(opts: {
  selectedInternals: ChangeWorkGroup[];
  selectedExternals: ChangeWorkGroup[];
  existingInternalKeys: Set<string>;
  existingExternalIds: Set<number>;
}): { newInternals: ChangeWorkGroup[]; newExternals: ChangeWorkGroup[] } {
  const newInternals = opts.selectedInternals.filter(
    (slot) => !opts.existingInternalKeys.has(slotReviewKey(slot)),
  );
  const newExternals = opts.selectedExternals.filter(
    (group) => !opts.existingExternalIds.has(group.group_id),
  );
  return { newInternals, newExternals };
}

/**
 * Redisparo manual: dispara linhas selecionadas (não bloqueadas), sem regravar catálogo.
 * Adicionar subtarefas (sem prefill): todas as selecionadas.
 * Demais fluxos: só linhas que ainda não ocupam a mudança.
 */
export function resolvePackageSelectionsForSave(opts: {
  unrestrictedManualCreate: boolean;
  prefillLikeCreate: boolean;
  selectedInternals: ChangeWorkGroup[];
  selectedExternals: ChangeWorkGroup[];
  internalDrafts: Record<string, InternalReviewDraft>;
  externalDrafts: Record<string, ExternalReviewDraft>;
  existingInternalKeys: Set<string>;
  existingExternalIds: Set<number>;
}): { newInternals: ChangeWorkGroup[]; newExternals: ChangeWorkGroup[] } {
  if (opts.unrestrictedManualCreate) {
    if (opts.prefillLikeCreate) {
      const newInternals = opts.selectedInternals.filter((slot) => {
        const key = slot.review_key || slotReviewKey(slot);
        const draft = opts.internalDrafts[key];
        if (draft?.locked) return false;
        return draft?.selected !== false;
      });
      const newExternals = opts.selectedExternals.filter((group) => {
        const key = group.review_key || externalLineKey(group);
        const draft = opts.externalDrafts[key];
        if (draft?.locked) return false;
        return draft?.selected !== false;
      });
      return { newInternals, newExternals };
    }
    return {
      newInternals: opts.selectedInternals,
      newExternals: opts.selectedExternals,
    };
  }
  return filterNewPackageSelections({
    selectedInternals: opts.selectedInternals,
    selectedExternals: opts.selectedExternals,
    existingInternalKeys: opts.existingInternalKeys,
    existingExternalIds: opts.existingExternalIds,
  });
}

export async function enrichInternalDraftFromTemplate(
  slot: ChangeWorkGroup,
  draft: InternalReviewDraft,
): Promise<InternalReviewDraft> {
  const templateId = slot.internal_subtask_template_id;
  if (!templateId || templateId <= 0 || draft.locked) {
    return draft;
  }
  if ((draft.questions || []).length > 0) {
    return draft;
  }
  try {
    const data = await changesApi.listInternalSubtaskTemplateFields(templateId);
    const items = data.items || [];
    if (!items.length) {
      return draft;
    }
    const questions = templateFieldsToReviewQuestions(templateId, items);
    const templateTitle = (slot.internal_subtask_template_name || '').trim();
    return {
      ...draft,
      title: (draft.title || templateTitle || slot.name || '').trim() || draft.title,
      questions,
    };
  } catch {
    return draft;
  }
}

export async function persistInternalTemplateDraftsBeforePackage(opts: {
  slots: ChangeWorkGroup[];
  internalDrafts: Record<string, InternalReviewDraft>;
  templatePersistMode: InternalSubtaskTemplatePersistMode;
  currentUserId?: number;
  isChangeManager: boolean;
  allowRequesterPackageEdit: boolean;
}): Promise<Array<{ templateId: number; groupId: number }>> {
  const saved: Array<{ templateId: number; groupId: number }> = [];
  for (const slot of opts.slots) {
    const key = slotReviewKey(slot);
    const draft = opts.internalDrafts[key] ?? emptyInternalDraft(slot);
    if (draft.locked) continue;
    const canPersist = canMutateInternalPackageSlot(slot, {
      currentUserId: opts.currentUserId,
      isChangeManager: opts.isChangeManager,
      allowRequesterPackageEdit: opts.allowRequesterPackageEdit,
    });
    const templateId = await persistInternalDraftIfNeeded({
      slot,
      draft,
      mode: opts.templatePersistMode,
      canPersist,
    });
    if (templateId) {
      saved.push({ templateId, groupId: slot.group_id });
    }
  }
  return saved;
}

export async function uploadSubtaskPackageFiles(
  changeId: number,
  subtasks: ChangeSubtask[],
  preparedExternals: Record<string, ExternalReviewDraft>,
) {
  const incidentSubtasks = (subtasks || []).filter((item) => item.origin === 'incidente');
  const usedSubtaskIds = new Set<number>();
  for (const [lineKey, draft] of Object.entries(preparedExternals)) {
    const groupId = parseExternalLineKey(lineKey).groupId;
    const files = draft.files || [];
    const customFiles: IncidentCustomFieldFiles = draft.customFieldFiles || {};
    const hasCustomFiles = Object.values(customFiles).some((value) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value),
    );
    if (!files.length && !hasCustomFiles) continue;
    const subtask = incidentSubtasks.find(
      (item) => item.group_id === groupId && item.id != null && !usedSubtaskIds.has(item.id),
    );
    if (!subtask?.id) continue;
    usedSubtaskIds.add(subtask.id);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      await changesApi.uploadSubtaskAttachment(changeId, subtask.id, formData);
    }
    const ticketId = subtask.ticket_id || subtask.incident?.ticket_id;
    if (ticketId) {
      await uploadIncidentCustomFieldFiles(ticketId, customFiles);
    }
  }
}
